/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {getConcessionariesAll} from '../../../../services/concessionariesService'
import {getInvoicesAllPagination} from '../../../../services/invoices'
import {getUnitConsumerAll} from '../../../../services/unitConsumerService'
import {Loading} from '../../../components/Loadings/loading'
import {useToast} from '../../../components/Toats'

import {ModalAddUpdateInvoice} from '../ModalAddEditInvoices'
import {ModalImportPreviousInvoices} from '../ModalImportPreviousInvoices'
import {ModalDeleteInvoice} from '../ModalDeleteInvoice'
import {ModalEditUnitConsumerMonitoring} from '../../../concessionaries/components/ModalEditUnitConsumerMonitoring'
import {ModalAddUpdateUnitConsumer} from '../../../unit-consumer/components/ModalAddEditUnitConsumer'
import {useAuth} from '../../../../modules/auth'
import {TableBody} from './TableBody'
import {BoardInvoices, groupInvoicesByPlant} from './BoardInvoices'
import {KTSVG} from '../../../../../_metronic/helpers'
import {getplantsAll} from '../../../../services/plantsService'
import {PaginationTablePlants} from '../../../dashboard/TablePlants/components/Pagination'
import listLayoutStyles from '../../../components/ListTableLayout/listTableLayout.module.css'
import {SelectGlobal} from '../../../components/SelectGlobal'
import {SearchInputV3} from '../../../components/SearchInputV3'
import DateSelector from '../../../reports/reportsDetails/TableReports/components/DateInput'
import headerStyles from './tableInvoices.module.css'

type Props = {
  className: string
}

type PaginationType = {
  page: number
  limit: number
  total: number
  qtdPages: number
}

const PAGE_SIZE = 10
const BOARD_PAGE_SIZE = 1000
const BOARD_PLANTS_PAGE_SIZE = 12
type ViewMode = 'table' | 'board'

const TableInvoices: React.FC<Props> = ({className}) => {
  const user = useAuth()
  const {toggleToast} = useToast()

  const [showCreateAppModal, setShowCreateAppModal] = useState(false)
  const [showImportPreviousInvoicesModal, setShowImportPreviousInvoicesModal] = useState(false)
  const [showCreateAppModalDelete, setShowCreateAppModalDelete] = useState(false)
  const [myInvoice, setMyInvoice] = useState<any | null>(null)
  const [concessionaireModal, setConcessionaireModal] = useState<{
    concessionaire: any
    unitConsumerId: string
  } | null>(null)
  const [unitConsumerModalId, setUnitConsumerModalId] = useState<string | null>(null)

  const canEdit = !!user.currentUser?.role?.slug && user.currentUser.role.slug !== 'CLIENTE'

  const handleOpenConcessionaire = (unitConsumer: any) => {
    if (!canEdit || !unitConsumer?.concessioraire) return
    setConcessionaireModal({
      concessionaire: unitConsumer.concessioraire,
      unitConsumerId: unitConsumer.id,
    })
  }

  const handleOpenUnitConsumer = (unitConsumer: any) => {
    if (!canEdit || !unitConsumer?.id) return
    setUnitConsumerModalId(unitConsumer.id)
  }

  // Filtros Unificados
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedUnitConsumer, setSelectedUnitConsumer] = useState('')
  const [selectedConcessionaries, setSelectedConcessionaries] = useState('')
  const [filterPlants, setFilterPlants] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterStatusUnitConsumer, setFilterStatusUnitConsumer] = useState('')
  const [filterDenomination, setFilterDenomination] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('table')

  // Paginação tipada resolve o erro no componente filho
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    qtdPages: 1,
  })

  const [boardPagination, setBoardPagination] = useState<PaginationType>({
    page: 1,
    limit: BOARD_PLANTS_PAGE_SIZE,
    total: 0,
    qtdPages: 1,
  })

  const {
    data: unitConsumers = [],
    isError: isErrorUnit,
    isLoading: loadingUnit,
  } = useQuery<any[]>({
    queryKey: ['unitConsumers'],
    queryFn: async () => await getUnitConsumerAll(),
    staleTime: 1000 * 60 * 5,
  })

  const {
    data: concessionaries = [],
    isError: isErrorConcessionaries,
    isLoading: loadingConcessionaries,
  } = useQuery<any[]>({
    queryKey: ['concessionaries'],
    queryFn: async () => await getConcessionariesAll(),
    staleTime: 1000 * 60 * 5,
  })

  const {data: plants = [], isError: isErrorPlants} = useQuery<any[]>({
    queryKey: ['plants'],
    queryFn: async () => await getplantsAll(),
    staleTime: 1000 * 60 * 5,
  })

  const {
    data: invoiceResponse,
    isLoading: loading,
    refetch: refreshInvoice,
    isError: isErrorInvoice,
  } = useQuery<any>({
    queryKey: [
      'getInvoice',
      selectedDate,
      selectedUnitConsumer,
      selectedConcessionaries,
      pagination.page,
      pagination.limit,
      filterPlants,
      filterStatus,
      filterStatusUnitConsumer,
      filterDenomination,
    ],
    queryFn: async () =>
      await getInvoicesAllPagination(
        selectedUnitConsumer,
        selectedDate,
        selectedConcessionaries,
        filterPlants,
        filterStatus,
        filterStatusUnitConsumer,
        undefined,
        filterDenomination || undefined,
        pagination.limit,
        pagination.page
      ),
    enabled: viewMode === 'table',
    placeholderData: (previousData: any) => previousData,
  })

  const {
    data: boardInvoiceResponse,
    isLoading: loadingBoard,
    refetch: refreshBoardInvoice,
    isError: isErrorBoardInvoice,
  } = useQuery<any>({
    queryKey: [
      'getInvoiceBoard',
      selectedDate,
      selectedUnitConsumer,
      selectedConcessionaries,
      filterPlants,
      filterStatus,
      filterStatusUnitConsumer,
      filterDenomination,
    ],
    queryFn: async () =>
      await getInvoicesAllPagination(
        selectedUnitConsumer,
        selectedDate,
        selectedConcessionaries,
        filterPlants,
        filterStatus,
        filterStatusUnitConsumer,
        undefined,
        filterDenomination || undefined,
        BOARD_PAGE_SIZE,
        1
      ),
    enabled: viewMode === 'board',
    placeholderData: (previousData: any) => previousData,
  })

  useEffect(() => {
    if (isErrorUnit) toggleToast('Erro ao buscar as unidades consumidoras!', 'error')
    if (isErrorConcessionaries) toggleToast('Erro ao buscar as concessionárias!', 'error')
    if (isErrorPlants) toggleToast('Erro ao buscar as usinas!', 'error')
    if (isErrorInvoice || isErrorBoardInvoice) toggleToast('Erro ao buscar as faturas!', 'error')
  }, [
    isErrorUnit,
    isErrorConcessionaries,
    isErrorPlants,
    isErrorInvoice,
    isErrorBoardInvoice,
    toggleToast,
  ])

  useEffect(() => {
    setBoardPagination((prev) => ({...prev, page: 1}))
  }, [
    selectedDate,
    selectedUnitConsumer,
    selectedConcessionaries,
    filterPlants,
    filterStatus,
    filterStatusUnitConsumer,
    filterDenomination,
  ])

  // Derivações diretas
  const invoices = invoiceResponse?.data || []
  const total = invoiceResponse?.total || 0

  const plantGroups = useMemo(
    () => groupInvoicesByPlant(boardInvoiceResponse?.data || []),
    [boardInvoiceResponse?.data]
  )
  const boardTotalPlants = plantGroups.length
  const boardQtdPages = Math.max(1, Math.ceil(boardTotalPlants / BOARD_PLANTS_PAGE_SIZE) || 1)
  const boardCurrentPage = Math.min(boardPagination.page, boardQtdPages)
  const pagedPlantGroups = plantGroups.slice(
    (boardCurrentPage - 1) * BOARD_PLANTS_PAGE_SIZE,
    boardCurrentPage * BOARD_PLANTS_PAGE_SIZE
  )
  const pagedBoardInvoices = pagedPlantGroups.flatMap((plant) =>
    plant.units.flatMap((unit) => unit.invoices)
  )

  const boardStart =
    boardTotalPlants === 0 ? 0 : (boardCurrentPage - 1) * BOARD_PLANTS_PAGE_SIZE + 1
  const boardEnd = Math.min(boardCurrentPage * BOARD_PLANTS_PAGE_SIZE, boardTotalPlants)
  const boardQtd = {start: boardStart, end: boardEnd}

  const boardPaginationView = {
    ...boardPagination,
    page: boardCurrentPage,
    limit: BOARD_PLANTS_PAGE_SIZE,
    total: boardTotalPlants,
    qtdPages: boardQtdPages,
  }

  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1
  const end = Math.min(pagination.page * pagination.limit, total)
  const currentQtd = {start, end: end === 0 && total > 0 ? pagination.limit : end}

  const tablePagination = {
    ...pagination,
    total,
    qtdPages: invoiceResponse?.lastPage || 1,
  }

  const activeFiltersCount = [
    selectedConcessionaries,
    selectedUnitConsumer,
    filterPlants,
    filterStatus,
    filterStatusUnitConsumer,
  ].filter((value) => value !== '' && value !== null && value !== undefined).length

  const refresh = () => {
    if (viewMode === 'board') {
      refreshBoardInvoice()
      return
    }
    refreshInvoice()
  }

  if (loadingUnit || loadingConcessionaries) {
    return (
      <div
        style={{display: 'flex', justifyContent: 'center', height: '30rem', alignItems: 'center'}}
      >
        <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
      </div>
    )
  }

  return (
    <div className={`card ${className} ${listLayoutStyles.listCard}`}>
      <div
        className={`border-0 ${listLayoutStyles.listCardHeader}`}
        style={{padding: '1.25rem 1.25rem 0'}}
      >
        <div className={`${listLayoutStyles.listToolbar} ${listLayoutStyles.listToolbarPanel}`}>
          <div className={listLayoutStyles.listToolbarFilters}>
            <div className={headerStyles.dateFilter}>
              <DateSelector selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
            </div>

            <SearchInputV3
              className={`form-control form-control-sm ${listLayoutStyles.listSearch}`}
              placeholder={'Buscar por denominação'}
              setMySearch={setFilterDenomination}
              setPagination={setPagination}
              style={{width: '100%'}}
            />

            <div className='dropdown'>
              <button
                type='button'
                className='btn btn-lg btn-light btn-active-color-primary btn-sm dropdown-toggle position-relative'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                data-bs-auto-close='outside'
                title='Filtros'
              >
                <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-3' />
                {activeFiltersCount > 0 && (
                  <span className='position-absolute top-0 start-100 translate-middle badge badge-circle badge-primary'>
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <div
                className={`dropdown-menu dropdown-menu-end p-4 ${listLayoutStyles.listFilterDropdownMenu}`}
              >
                <label
                  className='form-label fw-bold'
                  style={{marginTop: '0.5rem', marginBottom: '0.5rem'}}
                >
                  Filtros
                </label>
                <div className='separator my-2'></div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                  <SelectGlobal
                    itens={concessionaries}
                    buttonAll={true}
                    textSearch={'Buscar concessionária'}
                    textButtonInit={'Filtrar por concessionária'}
                    labelKey='name'
                    valueKey='name'
                    typeIcon='badge'
                    badgeKey='status_mon_aut'
                    colorsBadge={{
                      MONITORADA: 'success',
                      'NÃO MONITORADA': 'danger',
                      'EM MANUTENÇÃO': 'info',
                    }}
                    value={selectedConcessionaries}
                    className={'w-100'}
                    setPagination={setPagination}
                    onChange={(value: string) => setSelectedConcessionaries(value ? value : '')}
                  />
                </div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                  <SelectGlobal
                    itens={unitConsumers}
                    buttonAll={true}
                    textSearch={'Buscar unidade consumidora'}
                    textButtonInit={'Filtrar por unidade consumidora'}
                    labelKey='name'
                    valueKey='id'
                    typeIcon='svg'
                    linkIcon='/media/icons/duotune/general/gen001.svg'
                    sizeIconSvg='2'
                    value={selectedUnitConsumer}
                    setPagination={setPagination}
                    onChange={(value: string) => setSelectedUnitConsumer(value)}
                  />
                </div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                  <SelectGlobal
                    itens={plants}
                    buttonAll={true}
                    textSearch={'Buscar usina'}
                    textButtonInit={'Filtrar por usina'}
                    labelKey='name'
                    valueKey='id'
                    typeIcon='img'
                    linkIcon='https://res.cloudinary.com/deq3ehxe3/image/upload/v1735238752/plant2_tuwshh.png'
                    value={filterPlants}
                    className={'w-100'}
                    setPagination={setPagination}
                    onChange={(value: string) => setFilterPlants(value)}
                  />
                </div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                  <SelectGlobal
                    itens={[{name: 'NÃO INFORMADO'}, {name: 'INFORMADO'}]}
                    buttonAll={true}
                    textSearch={'Buscar status'}
                    textButtonInit={'Filtrar por status'}
                    labelKey='name'
                    valueKey='name'
                    value={filterStatus}
                    className={'w-100'}
                    setPagination={setPagination}
                    onChange={(value: string) => setFilterStatus(value)}
                  />
                </div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                  <SelectGlobal
                    itens={[{name: 'ATIVA'}, {name: 'INATIVA'}]}
                    buttonAll={true}
                    textSearch={'Buscar mon.aut.'}
                    textButtonInit={'Filtrar por monitoramento automático'}
                    labelKey='name'
                    valueKey='name'
                    value={filterStatusUnitConsumer}
                    className={'w-100'}
                    setPagination={setPagination}
                    onChange={(value: string) => setFilterStatusUnitConsumer(value)}
                  />
                </div>

                <div className='separator my-2'></div>

                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <button
                    className='btn btn-color-gray-600 btn-active-color-primary'
                    onClick={() => {
                      setFilterPlants('')
                      setSelectedUnitConsumer('')
                      setSelectedConcessionaries('')
                      setFilterStatus('')
                      setFilterStatusUnitConsumer('')
                      setPagination((prev) => ({...prev, page: 1}))
                    }}
                  >
                    Limpar todos os filtros{' '}
                    <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-5' />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={`card-toolbar ${listLayoutStyles.listToolbarActions}`}>
            <div className='d-flex align-items-center gap-1 me-2'>
              <button
                type='button'
                className={`btn btn-sm btn-icon ${
                  viewMode === 'table' ? 'btn-light-primary' : 'btn-light'
                }`}
                onClick={() => setViewMode('table')}
                title='Visualização em tabela'
              >
                <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-2' />
              </button>
              <button
                type='button'
                className={`btn btn-sm btn-icon ${
                  viewMode === 'board' ? 'btn-light-primary' : 'btn-light'
                }`}
                onClick={() => setViewMode('board')}
                title='Visualização em grade'
              >
                <KTSVG path='/media/icons/duotune/general/gen025.svg' className='svg-icon-2' />
              </button>
            </div>

            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setShowImportPreviousInvoicesModal(true)}
              title='Click para importar faturas anteriores'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
              Importar Faturas Anteriores
            </button>
          </div>
        </div>
      </div>

      <div className={`card-body py-3 pb-2 ${listLayoutStyles.listTablePanel}`}>
        {viewMode === 'board' ? (
          loadingBoard ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '3rem',
              }}
            >
              <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
            </div>
          ) : (
            <>
              <BoardInvoices
                invoices={pagedBoardInvoices}
                setMyInvoice={setMyInvoice}
                setShowCreateAppModal={setShowCreateAppModal}
                setShowCreateAppModalDelete={setShowCreateAppModalDelete}
              />
              <PaginationTablePlants
                pagination={boardPaginationView}
                setPagination={setBoardPagination}
                qtd={boardQtd}
              />
            </>
          )
        ) : (
          <>
            <div className={`table-responsive ${listLayoutStyles.listTableWrap}`}>
              <table className='table table-row-dashed table-hover table-row-gray-300 align-middle gs-0 gy-4'>
                <thead className='mt-0 pt-0'>
                  <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                    <th className='min-w-250px'>Denominação</th>
                    <th className='min-w-150px'>Tipo UC</th>
                    <th className='min-w-150px'>Mês de Referência</th>

                    <th className='min-w-150px'>Leitura Atual</th>
                    <th className='min-w-150px'>Próxima Leitura</th>

                    <th className='min-w-140px'>Status</th>
                    <th className='min-w-200px'>Concessionária</th>
                    <th className='min-w-100px'>Mon. Aut.</th>
                    <th className='min-w-50px text-end'>Pagamento</th>
                    <th className='min-w-100px text-end'>Actions</th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={5} style={{textAlign: 'center', padding: '2rem'}}>
                        <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <TableBody
                    invoices={invoices}
                    setMyInvoice={setMyInvoice}
                    setShowCreateAppModal={setShowCreateAppModal}
                    setShowCreateAppModalDelete={setShowCreateAppModalDelete}
                    onOpenConcessionaire={handleOpenConcessionaire}
                    onOpenUnitConsumer={handleOpenUnitConsumer}
                    onPaymentUpdated={refresh}
                  />
                )}
              </table>
            </div>

            <PaginationTablePlants
              pagination={tablePagination}
              setPagination={setPagination}
              qtd={currentQtd}
            />
          </>
        )}
      </div>

      <ModalAddUpdateInvoice
        show={showCreateAppModal}
        id={myInvoice ? myInvoice.id : undefined}
        data={myInvoice ? myInvoice : undefined}
        handleClose={() => {
          setMyInvoice(null)
          setShowCreateAppModal(false)
          refresh()
        }}
      />

      <ModalImportPreviousInvoices
        show={showImportPreviousInvoicesModal}
        handleClose={() => {
          setShowImportPreviousInvoicesModal(false)
          refresh()
        }}
      />

      <ModalDeleteInvoice
        show={showCreateAppModalDelete}
        id={myInvoice ? myInvoice.id : undefined}
        data={myInvoice ? myInvoice : undefined}
        handleClose={() => {
          setMyInvoice(null)
          setShowCreateAppModalDelete(false)
          refresh()
        }}
      />

      <ModalEditUnitConsumerMonitoring
        show={!!concessionaireModal}
        concessionaire={concessionaireModal?.concessionaire ?? null}
        unitConsumerId={concessionaireModal?.unitConsumerId}
        handleClose={() => {
          setConcessionaireModal(null)
          refresh()
        }}
      />

      <ModalAddUpdateUnitConsumer
        show={!!unitConsumerModalId}
        id={unitConsumerModalId ?? undefined}
        handleClose={() => {
          setUnitConsumerModalId(null)
          refresh()
        }}
      />
    </div>
  )
}

export {TableInvoices}
